/**
 * Dinamik breadcrumb oluşturucu utility
 */

// Türkçe karakterler için güvenli capitalize fonksiyonu
function capitalizeFirstLetter(str) {
  if (!str || typeof str !== "string") return str;

  // İlk karakteri büyük harfe çevir, geri kalanını küçük harfe çevir
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

// Özel isimler için capitalize fonksiyonu (birden fazla kelime için)
function capitalizeName(str) {
  if (!str || typeof str !== "string") return str;

  // Tire, boşluk veya nokta ile ayrılmış kelimeleri capitalize et
  return str
    .split(/[\s\-\.]+/)
    .map((word) => capitalizeFirstLetter(word))
    .join(" ");
}

// Sayfa türlerine göre breadcrumb şablonları
// t: translation function (breadcrumb namespace)
const BREADCRUMB_TEMPLATES = {
  // Ana sayfa
  home: (params = {}, t = null) => [
    { label: t ? t("brandName") : "Turna", href: "/" },
  ],

  // Uçak bileti ana sayfası
  flightTicket: (params = {}, t = null) => [
    { label: t ? t("brandName") : "Turna", href: "/" },
    { label: t ? t("flightTicket") : "Uçak Bileti", href: "/flight" },
  ],

  // Şehir landing sayfası
  cityLanding: (params = {}, t = null) => {
    const { cityName, cityCode } = params;
    const formattedCity = cityName ? capitalizeName(cityName) : "İstanbul";

    return [
      { label: t ? t("brandName") : "Turna", href: "/" },
      { label: t ? t("flightTicket") : "Uçak Bileti", href: "/flight" },
      { label: formattedCity, href: `/flight/${cityName}` },
    ];
  },

  // Havayolu landing sayfası
  airlineLanding: (params = {}, t = null) => {
    const { airlineName } = params;
    const formattedAirline = airlineName
      ? capitalizeName(airlineName)
      : "Türk Hava Yolları";

    return [
      { label: t ? t("brandName") : "Turna", href: "/" },
      { label: t ? t("flightTicket") : "Uçak Bileti", href: "/flight" },
      { label: formattedAirline, href: `/flight/${airlineName}` },
    ];
  },

  // Rota landing sayfası
  routeLanding: (params = {}, t = null) => {
    const { origin, destination } = params;
    const formattedOrigin = origin ? capitalizeName(origin) : "İstanbul";
    const formattedDestination = destination
      ? capitalizeName(destination)
      : "Ankara";

    return [
      { label: t ? t("brandName") : "Turna", href: "/" },
      { label: t ? t("flightTicket") : "Uçak Bileti", href: "/flight" },
      {
        label: `${formattedOrigin} - ${formattedDestination}`,
        href: `/flight/${origin}-${destination}`,
      },
    ];
  },

  // Havalimanı landing sayfası
  airportLanding: (params = {}, t = null) => {
    const { airportName, airportCode } = params;
    const formattedAirport = airportName
      ? capitalizeName(airportName)
      : "İstanbul Havalimanı";

    return [
      { label: t ? t("brandName") : "Turna", href: "/" },
      { label: t ? t("flightTicket") : "Uçak Bileti", href: "/flight" },
      { label: formattedAirport, href: `/flight/${airportName}` },
    ];
  },

  // Kampanya sayfası
  campaign: (params = {}, t = null) => {
    const { campaignName } = params;
    const items = [
      { label: t ? t("brandName") : "Turna", href: "/" },
      { label: t ? t("flightTicket") : "Uçak Bileti", href: "/flight" },
    ];

    if (campaignName) {
      items.push({
        label: capitalizeName(campaignName),
        href: `/flight/campaigns/${campaignName}`,
      });
    }

    return items;
  },
};

/**
 * Sayfa türüne göre breadcrumb oluşturur
 * @param {string} pageType - Sayfa türü (home, flightTicket, cityLanding, etc.)
 * @param {object} params - Sayfa parametreleri
 * @param {function} t - Translation function (breadcrumb namespace)
 * @returns {Array} Breadcrumb items array
 */
export function generateBreadcrumbs(pageType, params = {}, t = null) {
  const template = BREADCRUMB_TEMPLATES[pageType];

  if (!template) {
    console.warn(`Breadcrumb template not found for page type: ${pageType}`);
    return BREADCRUMB_TEMPLATES.home({}, t);
  }

  try {
    return template(params, t);
  } catch (error) {
    console.error(`Error generating breadcrumbs for ${pageType}:`, error);
    return BREADCRUMB_TEMPLATES.home({}, t);
  }
}

/**
 * URL pathname'den otomatik breadcrumb oluşturur
 * @param {string} pathname - URL pathname
 * @param {object} pageData - Sayfa verisi (opsiyonel)
 * @param {function} t - Translation function (breadcrumb namespace)
 * @returns {Array} Breadcrumb items array
 */
export function generateBreadcrumbsFromPath(pathname, pageData = {}, t = null) {
  const segments = pathname.split("/").filter(Boolean);

  // Ana sayfa
  if (segments.length === 0) {
    return generateBreadcrumbs("home", {}, t);
  }

  // Uçak bileti ana sayfası
  if (segments[0] === "flight" && segments.length === 1) {
    return generateBreadcrumbs("flightTicket", {}, t);
  }

  // Landing sayfaları
  if (segments[0] === "flight" && segments.length === 2) {
    const landingPageName = segments[1];

    // Sayfa türünü belirle
    if (pageData?.Type) {
      switch (pageData.Type) {
        case "city":
          return generateBreadcrumbs(
            "cityLanding",
            {
              cityName: pageData.City || landingPageName,
            },
            t,
          );
        case "airline":
          return generateBreadcrumbs(
            "airlineLanding",
            {
              airlineName: pageData.Airline || landingPageName,
            },
            t,
          );
        case "route":
          return generateBreadcrumbs(
            "routeLanding",
            {
              origin: pageData.Origin,
              destination: pageData.Destination,
            },
            t,
          );
        case "airport":
          return generateBreadcrumbs(
            "airportLanding",
            {
              airportName: pageData.AirportName || landingPageName,
            },
            t,
          );
        default:
          return generateBreadcrumbs("flightTicket", {}, t);
      }
    }

    // Fallback: basit landing sayfası
    return [
      { label: t ? t("brandName") : "Turna", href: "/" },
      { label: t ? t("flightTicket") : "Uçak Bileti", href: "/flight" },
      {
        label: capitalizeName(landingPageName),
        href: `/flight/${landingPageName}`,
      },
    ];
  }

  // Kampanya sayfaları
  if (segments[0] === "flight" && segments[1] === "campaigns") {
    const campaignName = segments[2];
    return generateBreadcrumbs("campaign", { campaignName }, t);
  }

  // Varsayılan
  return generateBreadcrumbs("home", {}, t);
}

/**
 * Çeviri desteği ile breadcrumb oluşturur
 * @deprecated Bu fonksiyon artık kullanılmıyor. generateBreadcrumbs fonksiyonu doğrudan translation function'ı kabul ediyor.
 * @param {string} pageType - Sayfa türü
 * @param {object} params - Sayfa parametreleri
 * @param {function} t - Çeviri fonksiyonu (breadcrumb namespace)
 * @returns {Array} Breadcrumb items array
 */
export function generateBreadcrumbsWithTranslations(
  pageType,
  params = {},
  t = null,
) {
  // Artık generateBreadcrumbs doğrudan translation function'ı kabul ediyor
  return generateBreadcrumbs(pageType, params, t);
}
